/* eslint-disable max-lines */
import {
    Box,
    Button,
    ChakraProps,
    Container,
    Flex,
    HStack,
    Heading,
    Skeleton,
    Text,
    VStack,
} from '@chakra-ui/react';
import React, {
    ComponentProps,
    ReactElement,
} from 'react';
import {
    Hide,
    Show,
} from '../../components';
import { ThemeTokens } from '../../theme/tokens';

const styles = {
    border: '1px',
    borderColor: ThemeTokens.Defaults.borderColor,
    bgColor: 'white',
    px: ThemeTokens.Defaults.px,
    py: ThemeTokens.Defaults.py,
};

export function PropertyDetailsLayout({ children }: { children?: React.ReactNode }) {
    return (
        <Box justifyContent="initial" w="full" minH="100vh">
            <Flex>{children}</Flex>
        </Box>
    );
}

function Content({ children }: { children?: React.ReactNode }) {
    return (
        <VStack w="full" spacing={0}>
            {children}
        </VStack>
    );
}

function Controls({ children }: { children?: React.ReactNode }) {
    return (
        <Box
            px={styles.px}
            py={[3, null, 4]}
            w="full"
            bgColor={styles.bgColor}
            borderBottom={styles.border}
            borderColor={styles.borderColor}
        >
            {children}
        </Box>
    );
}

function TitleBreadcrumbs({ children }: { children?: React.ReactNode }) {
    return (
        <Box color="gray.500" mb={6}>
            {children}
        </Box>
    );
}

function TitleAside(
    {
        children,
        mobileAside = false,
    }: {
        mobileAside?: boolean;
        children?: React.ReactNode;
    }
) {
    return (
        <Box
            w={mobileAside ? ['auto'] : ['full', null, null, 'auto']}
            borderBottom={mobileAside ? 'none' : styles.border}
            bgColor="white"
            borderColor={styles.borderColor}
            py={mobileAside ? 0 : [3, null, null, 0]}
            pl={mobileAside ? 0 : [5, null, null, 0]}
        >
            {children}
        </Box>
    );
}

function TitleHeader({
    children,
    ...props
}: {
    children?: React.ReactNode;
} & ComponentProps<typeof Heading>) {
    return (
        <Heading
            as="h1"
            size="xl"
            mb={3}
            {...props}
        >
            {children}
        </Heading>
    );
}

function TitleDescription({
    children,
}: {
    children?: React.ReactNode;
}) {
    return (
        <Text as="div">
            {children}
        </Text>
    );
}

function TitleSkeleton({
    breadcrumbs = false,
}: {
    breadcrumbs?: boolean;
}) {
    return (
        <>
            {
                breadcrumbs && (
                    <PropertyDetailsLayout.Title.Breadcrumbs>
                        <Skeleton opacity={0.3} sx={{ animationDuration: '2s' }}>
                            Breadcrumb / Breadcrumb
                        </Skeleton>
                    </PropertyDetailsLayout.Title.Breadcrumbs>
                )
            }
            <PropertyDetailsLayout.Title.Subheading>
                <Skeleton sx={{ animationDuration: '2s' }}>
                    Subheading
                </Skeleton>
            </PropertyDetailsLayout.Title.Subheading>
            <PropertyDetailsLayout.Title.Header>
                <Skeleton sx={{ animationDuration: '2s' }}>
                    10 Test Street, Testington VIC 3000
                </Skeleton>
            </PropertyDetailsLayout.Title.Header>
            <PropertyDetailsLayout.Title.Description>
                <Skeleton sx={{ animationDuration: '2s' }}>
                    Property manager: John Doe
                </Skeleton>
            </PropertyDetailsLayout.Title.Description>
        </>
    );
}

function TitleSubheading({
    children,
    color = 'primary.500',
}: {
    children?: React.ReactNode;
    color?: ChakraProps['color'];
}) {
    return (
        <Heading
            pb={3}
            fontSize="0.8em"
            color={color}
            letterSpacing={1.7}
            fontWeight="bold"
        >
            {children}
        </Heading>
    );
}

function Title({
    children,
    mobileAside = false,
}: {
    children?: React.ReactNode;
    mobileAside?: boolean;
}) {
    return (
        <Flex
            w="full"
            bgColor={styles.bgColor}
            borderBottom={styles.border}
            borderColor={styles.borderColor}
            direction={mobileAside ? 'row' : ['column', null, 'row']}
            justifyContent="space-between"
            alignItems={mobileAside ? 'center' : ['stretch', null, 'center']}
            gap={mobileAside ? [2, null, 4] : [0, null, 4]}
        >
            <Container
                as={VStack}
                maxW="80em"
                alignItems="start"
                py={8}
                px={5}
                gap={0}
            >
                <HStack width="100%" alignItems="start" gap={6}>
                    <VStack
                        alignItems="start"
                        gap={0}
                        flexGrow="1"
                    >
                        {/**
                     * This little bit of nastiness lets us pick up components
                     * from children and place them in different spots
                     * based on their defined types
                    */}
                        {React.Children.toArray(children)
                            .filter((child) => (child as ReactElement).type === TitleBreadcrumbs)}
                        {React.Children.toArray(children)
                            .filter((child) => (child as ReactElement).type === TitleSubheading)}
                        {React.Children.toArray(children)
                            .filter((child) => (child as ReactElement).type === TitleHeader)}
                        {React.Children.toArray(children)
                            .filter((child) => (child as ReactElement).type === TitleSkeleton)}
                    </VStack>
                    <VStack
                        alignItems="end"
                        gap={0}
                        flexGrow="1"
                    >
                        {React.Children.toArray(children)
                            .filter((child) => (child as ReactElement).type === TitleAside)}
                    </VStack>
                </HStack>
                {React.Children.toArray(children)
                    .filter((child) => (child as ReactElement).type === TitleDescription)}
            </Container>
            {React.Children.toArray(children)
                .filter((child) => (child as ReactElement).type !== TitleBreadcrumbs
                    && (child as ReactElement).type !== TitleSubheading
                    && (child as ReactElement).type !== TitleHeader
                    && (child as ReactElement).type !== TitleDescription
                    && (child as ReactElement).type !== TitleSkeleton
                    && (child as ReactElement).type !== TitleAside)}
        </Flex>
    );
}

function Body({ children }: { children?: React.ReactNode }) {
    return (
        <Container
            as={VStack}
            maxW="80em"
            alignItems="start"
            py={8}
            px={5}
            gap={6}
        >
            {children}
        </Container>
    );
}

function BodySkeleton() {
    return (
        <Container
            as={VStack}
            maxW="80em"
            alignItems="start"
            px={0}
            gap={6}
            w="full"
        >
            <Skeleton height={16} width="100%" sx={{ animationDuration: '2s' }} />
            <Skeleton minH="80vh" width="100%" sx={{ animationDuration: '2.2s' }} />
        </Container>
    );
}

function ControlsSkeleton() {
    return (
        <Container
            as={VStack}
            maxW="80em"
            alignItems="start"
            px={[0, null, null, null, 5]}
            gap={6}
        >
            <HStack align="left">
                <Hide above="md">
                    <Skeleton opacity={0.3} ml="auto" sx={{ animationDuration: '1.8s' }}>
                        <Button size="sm">Edit property</Button>
                    </Skeleton>
                    <Skeleton opacity={0.3} ml="auto" sx={{ animationDuration: '1.8s' }}>
                        <Button size="sm">New work order</Button>
                    </Skeleton>
                </Hide>
                <Show above="md">
                    <Skeleton opacity={0.3} sx={{ animationDuration: '1.8s' }}>
                        <Button size="sm">Edit property</Button>
                    </Skeleton>
                    <Skeleton opacity={0.3} sx={{ animationDuration: '1.8s' }}>
                        <Button size="sm">New work order</Button>
                    </Skeleton>
                </Show>
            </HStack>
        </Container>
    );
}

function TitleAsideSkeleton() {
    return (
        <Show above="md">
            <Container
                as={VStack}
                maxW="80em"
                alignItems="end"
                px={0}
                gap={6}
            >
                <Skeleton sx={{ animationDuration: '2s' }}>
                    <Button size={TitleAside.Size}>Call to action</Button>
                </Skeleton>
            </Container>
        </Show>
    );
}

function TabSkeleton() {
    return (
        <HStack alignItems="start" w="full">
            <HStack align="left">
                <Hide above="md">
                    <Skeleton w="100%" opacity={0.3} ml="auto" sx={{ animationDuration: '1.8s' }}>
                        <Button size="sm">Dropdown</Button>
                    </Skeleton>
                </Hide>

                <Show above="md">
                    <Skeleton w={120} opacity={0.3} sx={{ animationDuration: '2s' }}>
                        <Button w={20} size="sm">Tab 1</Button>
                    </Skeleton>
                    <Skeleton w={120} opacity={0.4} sx={{ animationDuration: '1.4s' }}>
                        <Button w={20} size="sm">Tab 2</Button>
                    </Skeleton>
                    <Skeleton w={120} opacity={0.2} sx={{ animationDuration: '1.8s' }}>
                        <Button w={20} size="sm">Tab 3</Button>
                    </Skeleton>
                    <Skeleton w={120} opacity={0.3} sx={{ animationDuration: '1.6s' }}>
                        <Button w={20} size="sm">Tab 4</Button>
                    </Skeleton>
                </Show>
            </HStack>
        </HStack>
    );
}

function TabContentSkeleton() {
    return (
        <VStack alignItems="start" opacity={0.4} w="full" gap={4}>
            <Skeleton height={28} width="100%" sx={{ animationDuration: '2s' }} />
            <Skeleton height={28} width="100%" sx={{ animationDuration: '2.4s' }} />
        </VStack>
    );
}

function Tabs({ children }: { children: React.ReactNode }) {
    return (
        <VStack w="full" gap={6}>
            {children}
        </VStack>
    );
}

function PropertyDetailsSkeleton({
    aside = false,
    breadcrumbs = true,
    controls = false,
}: {
    aside?: boolean;
    breadcrumbs?: boolean;
    controls?: boolean;
}) {
    return (
        <PropertyDetailsLayout>
            <PropertyDetailsLayout.Content>
                <PropertyDetailsLayout.Title>
                    <PropertyDetailsLayout.Title.Skeleton breadcrumbs={breadcrumbs} />
                    {
                        aside && (
                            <PropertyDetailsLayout.Title.Aside>
                                <PropertyDetailsLayout.Title.Aside.Skeleton />
                            </PropertyDetailsLayout.Title.Aside>
                        )
                    }
                </PropertyDetailsLayout.Title>
                {
                    controls && (
                        <PropertyDetailsLayout.Controls>
                            <PropertyDetailsLayout.Controls.Skeleton />
                        </PropertyDetailsLayout.Controls>
                    )
                }
                <PropertyDetailsLayout.Body>
                    <Tabs.Body>
                        <Tabs.Skeleton />
                        <Tabs.ContentSkeleton />
                    </Tabs.Body>
                </PropertyDetailsLayout.Body>
            </PropertyDetailsLayout.Content>
        </PropertyDetailsLayout>
    );
}

Body.Skeleton = BodySkeleton;
Controls.Skeleton = ControlsSkeleton;
TitleAside.Size = ['sm', null, 'cta-md'];
TitleAside.Skeleton = TitleAsideSkeleton;

Title.Aside = TitleAside;
Title.Breadcrumbs = TitleBreadcrumbs;
Title.Header = TitleHeader;
Title.Description = TitleDescription;
Title.Skeleton = TitleSkeleton;
Title.Subheading = TitleSubheading;

PropertyDetailsLayout.Title = Title;
PropertyDetailsLayout.Controls = Controls;
PropertyDetailsLayout.Content = Content;
PropertyDetailsLayout.Body = Body;
PropertyDetailsLayout.Skeleton = PropertyDetailsSkeleton;

Tabs.Body = Tabs;
Tabs.Skeleton = TabSkeleton;
Tabs.ContentSkeleton = TabContentSkeleton;
